
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Offer {
  id: string;
  title: string;
  description?: string;
  category?: string;
  image_url?: string;
  is_active: boolean;
  quantifiable?: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useOffers = (includeInactive = false) => {
  return useQuery({
    queryKey: ['offers', includeInactive],
    queryFn: async () => {
      console.log('🔄 Chargement des offres, inclure inactives:', includeInactive);
      
      let query = supabase
        .from('offers')
        .select('*')
        .order('created_at', { ascending: false }); // Tri par date de création décroissante

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Erreur lors de la récupération des offres:', error);
        throw error;
      }

      console.log('✅ Offres chargées avec catégories:', data?.map(o => ({ title: o.title, category: o.category })) || []);
      return data as Offer[];
    },
    staleTime: 0, // Force refresh to clear cache
    refetchOnWindowFocus: true
  });
};

export const useOfferMutations = () => {
  const queryClient = useQueryClient();

  const toggleOfferActive = useMutation({
    mutationFn: async ({ offerId, isActive }: { offerId: string; isActive: boolean }) => {
      const { data, error } = await supabase
        .from('offers')
        .update({ is_active: isActive })
        .eq('id', offerId)
        .select()
        .single();

      if (error) {
        console.error('❌ Erreur lors du changement de statut:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      toast.success(`Offre ${variables.isActive ? 'activée' : 'désactivée'} avec succès`);
    },
    onError: (error) => {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors du changement de statut de l\'offre');
    }
  });

  const toggleOfferQuantifiable = useMutation({
    mutationFn: async ({ offerId, quantifiable }: { offerId: string; quantifiable: boolean }) => {
      const { data, error } = await supabase
        .from('offers')
        .update({ quantifiable })
        .eq('id', offerId)
        .select()
        .single();

      if (error) {
        console.error('❌ Erreur lors du changement de quantifiable:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      toast.success(`Offre ${variables.quantifiable ? 'rendue quantifiable' : 'non quantifiable'} avec succès`);
    },
    onError: (error) => {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors du changement de quantifiable de l\'offre');
    }
  });

  const deleteOffer = useMutation({
    mutationFn: async (offerId: string) => {
      const { error } = await supabase
        .from('offers')
        .delete()
        .eq('id', offerId);

      if (error) {
        console.error('❌ Erreur lors de la suppression:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      toast.success('Offre supprimée avec succès');
    },
    onError: (error) => {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors de la suppression de l\'offre');
    }
  });

  const reorderOffers = useMutation({
    mutationFn: async (reorderedOffers: Offer[]) => {
      const updates = reorderedOffers.map((offer, index) => ({
        id: offer.id,
        display_order: index + 1
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('offers')
          .update({ display_order: update.display_order })
          .eq('id', update.id);

        if (error) {
          console.error('❌ Erreur lors de la mise à jour de l\'ordre:', error);
          throw error;
        }
      }

      return updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      toast.success('Ordre des offres mis à jour avec succès');
    },
    onError: (error) => {
      console.error('❌ Erreur lors de la réorganisation:', error);
      toast.error('Erreur lors de la réorganisation des offres');
    }
  });

  // Fonction pour normaliser automatiquement tous les ordres d'affichage
  const normalizeDisplayOrder = useMutation({
    mutationFn: async () => {
      // Récupérer toutes les offres actives triées par created_at
      const { data: offers, error } = await supabase
        .from('offers')
        .select('id, display_order, created_at')
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('❌ Erreur lors de la récupération des offres:', error);
        throw error;
      }

      if (!offers || offers.length === 0) {
        return [];
      }

      // Réassigner les ordres de 1 à n
      const updates = offers.map((offer, index) => ({
        id: offer.id,
        display_order: index + 1
      }));

      // Mettre à jour chaque offre
      for (const update of updates) {
        const { error } = await supabase
          .from('offers')
          .update({ display_order: update.display_order })
          .eq('id', update.id);

        if (error) {
          console.error('❌ Erreur lors de la normalisation:', error);
          throw error;
        }
      }

      return updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      toast.success('Ordre d\'affichage normalisé avec succès');
    },
    onError: (error) => {
      console.error('❌ Erreur lors de la normalisation:', error);
      toast.error('Erreur lors de la normalisation de l\'ordre d\'affichage');
    }
  });

  return {
    toggleOfferActive,
    toggleOfferQuantifiable,
    deleteOffer,
    reorderOffers,
    normalizeDisplayOrder
  };
};
